import { applyMiddleware, compose, createStore, type Store, type Dispatch, type AnyAction } from 'redux';
import createSagaMiddleware, { type SagaMiddleware } from 'redux-saga';
import { persistStore, type Persistor } from 'redux-persist';
import { connectRoutes, redirect } from 'redux-first-router';
import thunk from 'redux-thunk';
import queryString from 'query-string';
import { isEmpty } from 'lodash';
import { buildRootReducer } from './reducers';
import { sagas } from './sagas';
import { eventsTracker, initialDataFetcher, redirectRoutes, request, urlParamsSynchronizer } from './middlewares';
import { captureException } from '../tracker/exceptionTracker';
import { routesMap } from './routesMap';
import { PAGES } from '../utils/pages';
import { isUserAuthorized } from './selectors/user';
import { finishRehydration } from './actions/common';
import { DASHBOARD_TABS } from '../utils/constants';
import { hideBlockNavigationModal, showBlockNavigationModal } from './actions/ui';
import { type RootState, type ConfigureStoreReturn } from './types';

// Define types for actions
interface RouteAction {
    type: string;
    payload?: {
        section?: string;
        [key: string]: any;
    };
}

interface DashboardAction extends RouteAction {
    type: typeof PAGES.DASHBOARD;
    payload: {
        section: string;
    };
}

interface LoginAction extends RouteAction {
    type: typeof PAGES.LOGIN;
}

/**
 * onError => catch all errors uncaught in sagas
 *
 * https://redux-saga.js.org/docs/api/
 */
const sagaMiddleware: SagaMiddleware = createSagaMiddleware({
    onError: (error: Error): void => {
        console.error('Saga error captured', error);
        captureException(error);
    }
});

const dashboardScorecardAction: DashboardAction = {
    type: PAGES.DASHBOARD,
    payload: { section: DASHBOARD_TABS.i2x }
};

const loginAction: LoginAction = { type: PAGES.LOGIN };

const isLoginAction = (action: RouteAction): action is LoginAction => action.type === PAGES.LOGIN;

const isRecoveryAction = (action: RouteAction): boolean => action.type === PAGES.RECOVERY;

const isLoginOrHomeActionOrRecoveryAction = (action: RouteAction): boolean =>
    isLoginAction(action) || isRecoveryAction(action) || (action as any).type === PAGES.HOME;

const isDashboardWithoutSection = (action: RouteAction): boolean =>
    action.type === PAGES.DASHBOARD && isEmpty(action.payload?.section);

export const configureStore = (): ConfigureStoreReturn => {
    const conRoutesOpts = {
        querySerializer: queryString,
        initialDispatch: false,
        onBeforeChange: (
            dispatch: Dispatch<AnyAction>,
            getState: () => RootState,
            { action }: { action: RouteAction }
        ): void => {
            if (isUserAuthorized(getState())) {
                if (action.type === PAGES.BOT_CALL_HISTORY_REDIRECT || action.type === PAGES.CALL_HISTORY_REDIRECT) {
                    const callId = action.payload?.callId;
                    dispatch(
                        redirect({
                            type: PAGES.CALL_HISTORY_NEW,
                            payload: callId ? { callId } : {}
                        })
                    );
                } else if (isLoginOrHomeActionOrRecoveryAction(action) || isDashboardWithoutSection(action)) {
                    dispatch(redirect(dashboardScorecardAction));
                }
            } else if (!isLoginAction(action) && !isRecoveryAction(action)) {
                dispatch(redirect(loginAction));
            }
        },
        displayConfirmLeave: (message: string, callback: (canLeave: boolean) => void): void => {
            const canLeave = (can: boolean): void => {
                store.dispatch(hideBlockNavigationModal());
                callback(can);
            };

            store.dispatch(showBlockNavigationModal({ canLeave }));
        }
    };

    const {
        reducer: locationReducer,
        middleware: rrMiddleware,
        enhancer,
        initialDispatch
    } = connectRoutes(routesMap, conRoutesOpts);

    const middlewareEnhancer = applyMiddleware(
        thunk,
        request,
        redirectRoutes,
        rrMiddleware,
        eventsTracker,
        sagaMiddleware,
        initialDataFetcher,
        urlParamsSynchronizer
    );

    const enhancers = compose(enhancer, middlewareEnhancer);
    const store: Store<RootState> = createStore(buildRootReducer(locationReducer), undefined, enhancers) as any;

    const persistor: Persistor = persistStore(store, null, (): void => {
        if (initialDispatch) {
            initialDispatch();
        }
        store.dispatch(finishRehydration());
    });

    /* sagaMiddleware HAS to run after createStore */
    sagaMiddleware.run(sagas);
    return { store, persistor };
};
